import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/Button";
import { EventsMap } from "../components/EventsMap";
import {
  MascotMeeting,
  MascotScene,
  MascotTalking,
  MascotWalking,
  MascotWaving,
} from "../components/Mascot";
import { listPublicEvents, type PublicEventCard } from "../api/events";

const INTRO_KEY = "nifgash.seen-intro";

export function Landing({
  transparent = false,
  hideInlineMascots = false,
}: { transparent?: boolean; hideInlineMascots?: boolean } = {}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { auth } = useAuth();
  const authed = auth.kind === "user";
  const [events, setEvents] = useState<PublicEventCard[]>([]);
  const [playIntro, setPlayIntro] = useState(false);
  // In v2 mode section backgrounds become transparent so the sticky mascots
  // underneath are actually visible — solid bg-surface/bg-ink-50 would hide them.
  const surfaceBg = transparent ? "" : "bg-surface";
  const altBg = transparent ? "" : "bg-ink-50";

  useEffect(() => {
    listPublicEvents()
      .then((res) => setEvents(res.events))
      .catch(() => setEvents([]));
  }, []);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    if (!localStorage.getItem(INTRO_KEY)) {
      setPlayIntro(true);
      localStorage.setItem(INTRO_KEY, "1");
    }
  }, []);

  return (
    <div className={`min-h-screen ${surfaceBg}`}>
      {/* SCENE 1 — HERO: three mascots arrive together.
          In v2 the radial gradient is dropped so the journey figures
          (rendered at z=0 behind Landing content) read at full colour
          on the body's pale background instead of being tinted. */}
      <header
        className="relative overflow-hidden pt-10 pb-12 px-5"
        style={hideInlineMascots ? undefined : {
          background:
            "radial-gradient(ellipse 80% 60% at 50% 0%, var(--color-brand-purple-soft) 0%, transparent 70%)",
        }}
      >
        <div
          className={`flex justify-center mt-10 sm:mt-6 ${playIntro && !hideInlineMascots ? "nf-intro" : !hideInlineMascots ? "nf-idle" : ""}`}
          data-mascot-stop="hero"
          // mx-auto centres the fixed-width placeholder in the hero header
          // — without it the 300-px box sits at the left edge of the
          // header's content area and the trio reads "slightly off-centre".
          style={hideInlineMascots ? { width: 300, height: 200, marginLeft: "auto", marginRight: "auto" } : undefined}
        >
          {!hideInlineMascots && <MascotScene width={300} />}
        </div>

        <h1 className="text-[28px] sm:text-[40px] font-extrabold text-ink-900 leading-[1.1] text-center mt-6 tracking-tight max-w-[640px] mx-auto">
          {t("landing.heroTitle")}
        </h1>
        <p className="text-[15px] sm:text-[17px] text-ink-500 text-center mt-4 leading-relaxed max-w-[520px] mx-auto">
          {t("landing.heroSubtitleLine1")}
          <br className="hidden sm:inline" />
          {t("landing.heroSubtitleLine2")}
        </p>
        <p className="text-[12px] sm:text-[13px] text-ink-400 text-center mt-3 max-w-[420px] mx-auto">
          {t("landing.heroCities")}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 max-w-[360px] sm:max-w-none mx-auto">
          {authed ? (
            <Button size="lg" onClick={() => navigate("/events")}>
              {t("landing.ctaContinueToEvents")}
            </Button>
          ) : (
            <>
              <Button size="lg" onClick={() => navigate("/login")}>
                {t("landing.ctaLogin")}
              </Button>
              <Button size="lg" variant="secondary" onClick={() => navigate("/events")}>
                {t("landing.ctaListEvents")}
              </Button>
            </>
          )}
        </div>
      </header>

      {/* MAP TEASER — non-narrative section, just shows the goods */}
      <section className="px-4 sm:px-5 pt-10 sm:pt-14 pb-4 max-w-[920px] mx-auto">
        <h2 className="text-[20px] sm:text-[26px] font-extrabold text-ink-900 tracking-tight">
          {t("landing.whatsHappening")}
        </h2>
        <p className="text-[14px] text-ink-500 mt-1 mb-5">
          {events.length === 0
            ? t("landing.soonHere")
            : t("landing.eventCount", { count: events.length })}
        </p>
        <EventsMap
          events={events}
          height={320}
          onPopupCta={(id) => navigate(`/events/${id}`)}
        />
        <div className="mt-4 flex justify-center">
          <Button size="md" variant="secondary" onClick={() => navigate("/events")}>
            {t("landing.openFullList")}
          </Button>
        </div>
      </section>

      <NarrativeSection
        side="left"
        title={t("landing.scene1Title")}
        text={t("landing.scene1Text")}
        stickyMascot={hideInlineMascots}
        stopId="scene-1"
        mascotHeight={160}
        mascot={hideInlineMascots
          ? <PlaceholderBox w={140} h={160} />
          : <MascotWalking size={140} color="yellow" />}
      />

      <NarrativeSection
        side="right"
        bgClass={altBg}
        title={t("landing.scene2Title")}
        text={t("landing.scene2Text")}
        stickyMascot={hideInlineMascots}
        stopId="scene-2"
        mascotHeight={160}
        mascot={hideInlineMascots
          ? <PlaceholderBox w={260} h={160} />
          : <MascotMeeting width={260} />}
      />

      <NarrativeSection
        side="left"
        title={t("landing.scene3Title")}
        text={t("landing.scene3Text")}
        stickyMascot={hideInlineMascots}
        stopId="scene-3"
        mascotHeight={180}
        mascot={hideInlineMascots
          ? <PlaceholderBox w={260} h={180} />
          : <MascotTalking width={280} />}
      />

      <NarrativeSection
        side="right"
        bgClass={altBg}
        title={t("landing.scene4Title")}
        text={t("landing.scene4Text")}
        stickyMascot={hideInlineMascots}
        stopId="scene-4"
        mascotHeight={160}
        mascot={hideInlineMascots
          ? <PlaceholderBox w={140} h={160} />
          : <MascotWaving size={140} color="blue" />}
      />

      {/* CTA — final block on the landing. The copy and buttons swap based
          on whether the visitor is already signed in: anonymous users see
          the sign-in pitch; signed-in users get a single "back to events"
          CTA since the landing for them is just a preview, not a funnel. */}
      <section className={`px-5 py-14 ${surfaceBg} ${hideInlineMascots ? "pb-[14vh]" : ""}`}>
        <div className="text-center max-w-[480px] mx-auto">
          <h2 className="text-[22px] sm:text-[26px] font-extrabold text-ink-900 tracking-tight">
            {authed ? t("landing.ctaContinueToEvents") : t("landing.finalTitle")}
          </h2>
          {!authed && (
            <p className="text-[15px] text-ink-500 mt-3 leading-relaxed">
              {t("landing.finalText")}
            </p>
          )}
          {hideInlineMascots && (
            <div
              className="flex justify-center mt-8"
              data-mascot-stop="cta"
              style={{ width: 260, height: 174, margin: "32px auto 0" }}
            />
          )}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            {authed ? (
              <Button size="lg" onClick={() => navigate("/events")}>
                {t("landing.ctaListEvents")}
              </Button>
            ) : (
              <>
                <Button size="lg" onClick={() => navigate("/login")}>
                  {t("landing.ctaLogin")}
                </Button>
                <Button size="lg" variant="secondary" onClick={() => navigate("/events")}>
                  {t("landing.ctaListEvents")}
                </Button>
              </>
            )}
          </div>
        </div>
      </section>

      <footer className="px-5 py-8 text-center text-[12px] text-ink-400">
        <div>{t("landing.footer")}</div>
      </footer>

      {/* CSS — intro/idle/walk/meet/talk/wave keyframes.
          All animations respect prefers-reduced-motion via the @media block. */}
      <style>{`
        @keyframes nfIntro {
          0%   { transform: translateY(40px); opacity: 0; }
          60%  { transform: translateY(-6px); opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes nfIdle {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes nfWalk {
          0%   { transform: translateX(-40px) rotate(0deg); }
          25%  { transform: translateX(-12px) rotate(-3deg); }
          50%  { transform: translateX(0)     rotate(0deg); }
          75%  { transform: translateX(12px)  rotate(3deg); }
          100% { transform: translateX(40px)  rotate(0deg); }
        }
        @keyframes nfMeetLeft {
          0%   { transform: translateX(-80px); opacity: 0; }
          60%  { transform: translateX(8px); opacity: 1; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes nfMeetRight {
          0%   { transform: translateX(80px); opacity: 0; }
          60%  { transform: translateX(-8px); opacity: 1; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes nfBubble1 {
          0%, 100% { opacity: 1; transform: translateY(0); }
          25%      { opacity: 0.3; }
          50%      { opacity: 1; transform: translateY(-2px); }
        }
        @keyframes nfBubble2 {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          50%      { opacity: 1; transform: translateY(-2px); }
        }
        @keyframes nfWaveArm {
          0%, 100% { transform: rotate(0deg); }
          25%      { transform: rotate(-20deg); }
          75%      { transform: rotate(20deg); }
        }

        .nf-intro { animation: nfIntro 900ms ease-out both, nfIdle 4s ease-in-out 1s infinite; }
        .nf-idle  { animation: nfIdle 5s ease-in-out infinite; }

        /* Walk/meet/talk/wave only run when the section is on screen — we add the
           "nf-active" parent class via IntersectionObserver below. */
        .nf-active .nf-walk { animation: nfWalk 4s ease-in-out infinite; transform-origin: center bottom; }
        .nf-active .nf-meet-left  { animation: nfMeetLeft  1200ms ease-out both; }
        .nf-active .nf-meet-right { animation: nfMeetRight 1200ms ease-out both; }
        .nf-active .nf-bubble-1   { animation: nfBubble1 3s ease-in-out infinite; }
        .nf-active .nf-bubble-2   { animation: nfBubble2 3s ease-in-out infinite 1.5s; }
        .nf-active .nf-wave-arm   { animation: nfWaveArm 1.2s ease-in-out infinite; }

        @media (prefers-reduced-motion: reduce) {
          .nf-intro, .nf-idle,
          .nf-active .nf-walk, .nf-active .nf-meet-left, .nf-active .nf-meet-right,
          .nf-active .nf-bubble-1, .nf-active .nf-bubble-2, .nf-active .nf-wave-arm {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

/** A two-column section where the mascot lives on one side and text on the other.
 *  On mobile both stack vertically (mascot on top). The whole section gets an
 *  `nf-active` class once it scrolls into view, which triggers its animations. */
function NarrativeSection({
  title,
  text,
  mascot,
  side,
  bgClass = "",
  stickyMascot = false,
  stopId,
  mascotHeight = 160,
}: {
  title: string;
  text: string;
  mascot: React.ReactNode;
  side: "left" | "right";
  bgClass?: string;
  /** v2: pin the mascot to the centre of the viewport while the section is on screen */
  stickyMascot?: boolean;
  /** v2: data-mascot-stop attribute on the mascot column, so JourneyMascots can anchor here */
  stopId?: string;
  /** v2: placeholder height — used to compute the sticky `top` so the
   *  placeholder's centre lands exactly at viewport centre (vh/2 − h/2). */
  mascotHeight?: number;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || active) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(true);
            obs.disconnect();
            return;
          }
        }
      },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [active]);

  if (stickyMascot) {
    // v2 layout: on desktop (sm:) the section is tall (100vh), text and
    // mascot are flex siblings, mascot column stretches to row height (so
    // CSS sticky has room to actually stick within). On mobile we keep the
    // original column flow — sticky on a flex column item doesn't work
    // without giving the item explicit height which would push text aside.
    const reverse = side === "right";
    return (
      <section
        ref={ref}
        className={`px-5 py-12 sm:py-16 ${bgClass} ${active ? "nf-active" : ""} sm:min-h-screen`}
      >
        <div
          className={`max-w-[920px] mx-auto flex flex-col sm:flex-row gap-6 sm:gap-12 items-stretch sm:items-stretch ${
            reverse ? "sm:flex-row-reverse" : ""
          } sm:min-h-[calc(100vh-96px)]`}
        >
          <div className="flex-1 flex items-center">
            <div>
              <h2 className="text-[22px] sm:text-[28px] font-extrabold text-ink-900 tracking-tight leading-tight">
                {title}
              </h2>
              <p className="text-[15px] sm:text-[16px] text-ink-500 mt-4 leading-relaxed">{text}</p>
            </div>
          </div>
          <div
            className="sm:w-[320px] flex-shrink-0 flex justify-center sm:items-center sm:sticky sm:self-start"
            // Centre of the placeholder lands exactly at viewport centre,
            // so JourneyMascots's parkednessFor reaches 1 cleanly and the
            // trio is perfectly level at this parking.
            style={{ top: `calc(50vh - ${mascotHeight / 2}px)` }}
            data-mascot-stop={stopId}
          >
            {mascot}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={ref}
      className={`px-5 py-12 sm:py-16 ${bgClass} ${active ? "nf-active" : ""}`}
    >
      <div
        className={`max-w-[920px] mx-auto grid sm:grid-cols-2 gap-6 sm:gap-12 items-center ${
          side === "right" ? "sm:grid-flow-col-dense" : ""
        }`}
      >
        <div className={side === "right" ? "sm:col-start-2" : ""}>
          <h2 className="text-[22px] sm:text-[28px] font-extrabold text-ink-900 tracking-tight leading-tight">
            {title}
          </h2>
          <p className="text-[15px] sm:text-[16px] text-ink-500 mt-4 leading-relaxed">{text}</p>
        </div>
        <div className={`flex justify-center ${side === "right" ? "sm:col-start-1 sm:row-start-1" : ""}`}>
          <div data-mascot-stop={stopId}>{mascot}</div>
        </div>
      </div>
    </section>
  );
}

/** Reserves layout space where a big illustration would sit on /, so that
 *  JourneyMascots in /landing-v2 can read this anchor's coordinates and
 *  paint its single mascot layer aligned to the page flow. Renders nothing. */
function PlaceholderBox({ w, h }: { w: number; h: number }) {
  return <div style={{ width: w, height: h }} aria-hidden />;
}

